import { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from '../lib/gsap';
import { FadeUp, SlideInLeft, SlideInRight } from './Motion.jsx';
import { IconTarget, IconShield, IconHeart } from './Icons.jsx';
import MolecularBackground from './MolecularBackground.jsx';
import AnimatedHeroWave from './AnimatedHeroWave.jsx';
import heroPatient from '../assets/images/hero-patient.png';

const features = [
  { title: 'Targeted', desc: 'Activated only\nwhere the light\nreaches', Icon: IconTarget, tone: 'teal' },
  { title: 'Precise', desc: 'Minimally invasive\nwith maximum\nprecision', Icon: IconShield, tone: 'blue' },
  { title: 'Patient-focused', desc: 'Better outcomes\nand improved\nquality of life', Icon: IconHeart, tone: 'teal' },
];

export default function Hero() {
  const scope = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context((self) => {
      const mm = gsap.matchMedia();

      // Desktop only, and only when the user has not asked for reduced motion.
      // matchMedia handles both: if either condition fails the tween never runs
      // and the elements keep their normal painted position.
      mm.add('(prefers-reduced-motion: no-preference) and (min-width: 1024px)', () => {
        // Parallax the molecule CONTAINER, not the individual molecules —
        // those already carry their own CSS float animations on `transform`,
        // and animating the same property from two places would conflict.
        gsap.to(self.selector('.molecular-background'), {
          yPercent: 14,
          ease: 'none',
          scrollTrigger: {
            trigger: scope.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.6,
          },
        });
      });
    }, scope);

    // Kills the ScrollTrigger and strips any inline styles GSAP added,
    // so route changes don't leak triggers.
    return () => ctx.revert();
  }, []);

  return (
    <section className="fullbleed hero-v3" ref={scope}>
      {/* z-index 0 — molecular structures (parallaxed on desktop) */}
      <MolecularBackground variant="hero" />

      {/* z-index 1 — wave glow (own CSS drift animation, left untouched by GSAP) */}
      <AnimatedHeroWave />

      {/* z-index 2 — content */}
      <div className="hero-v3-inner">
        <SlideInLeft className="hero-v3-copy">
          <span className="hero-v3-eyebrow">LIGHT. SCIENCE. LIFE.</span>

          <h1 className="hero-v3-title">
            Advancing <span className="hero-v3-title-accent">light-activated</span>
            <br />
            cancer treatment in photodynamic therapy
          </h1>

          <p className="hero-v3-lede">
            Precision treatment, activated only where the light reaches.
            <br />
            Better outcomes. Improved quality of life.
          </p>

          <p className="hero-v3-sub">
            Photosensitizers, advanced light technology and an integrated
            delivery system &mdash; working as one.
          </p>

          <div className="hero-v3-cta-row">
            <Link to="/pdt" className="hero-v3-cta">
              See how the platform works <span aria-hidden="true">&rarr;</span>
            </Link>
            <Link to="/head-neck-cancer" className="hero-v3-cta-secondary">
              Head &amp; neck cancer <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </SlideInLeft>

        <div className="hero-v3-features">
          {features.map((f, i) => (
            <FadeUp key={f.title} delay={0.1 + i * 0.08} className="hero-v3-feature">
              <span className={`hero-v3-feature-icon hero-v3-feature-icon--${f.tone}`}>
                <f.Icon />
              </span>
              <p className="hero-v3-feature-title">{f.title}</p>
              <p className="hero-v3-feature-desc">{f.desc}</p>
            </FadeUp>
          ))}
        </div>

        <SlideInRight className="hero-v3-media">
          <img src={heroPatient} alt="Patient receiving light-activated photodynamic therapy" />
        </SlideInRight>
      </div>
    </section>
  );
}